import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { FdeCopy, TimelineActivity, TimelineEvent } from '@/frontend/features/Fde/types'
import type { TimelineActivityGroup } from '@/frontend/features/Fde/timelineActivityGroups'
import ManagementHistoryChanges from '@/frontend/features/Fde/ManagementHistoryChanges'

interface Props {
  group: TimelineActivityGroup
  events: TimelineEvent[]
  copy: FdeCopy
  canManage: boolean
  onEditEvent: (event: TimelineEvent) => void
}

type ItemProps = Omit<Props, 'group'> & { item: TimelineActivity }

function ActivitySubject({ item, events, canManage, onEditEvent }: ItemProps) {
  const event = item.kind === 'event' ? events.find((candidate) => candidate.id === item.target_id) : undefined

  if (event && canManage) {
    return <button type="button" onClick={() => onEditEvent(event)} className="font-semibold text-primary underline-offset-4 hover:underline">{item.subject}</button>
  }

  return item.subject ? <span className="font-semibold">{item.subject}</span> : null
}

function ActorAvatar({ activity }: { activity: TimelineActivity }) {
  return <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
    {activity.actor_avatar_url ? <img src={activity.actor_avatar_url} alt="" className="size-full object-cover" /> : activity.actor_name.charAt(0).toUpperCase()}
  </span>
}

function detailText(item: TimelineActivity) {
  if (!item.detail) return null
  if (item.kind !== 'event') return item.detail

  return format(parseISO(item.detail), "EEEE, d 'de' MMMM 'às' HH:mm", { locale: ptBR })
}

function ActivityDetail(props: ItemProps) {
  const detail = detailText(props.item)

  return <div className="grid grid-cols-[minmax(0,1fr)_3rem] gap-3 py-2 first:pt-0 last:pb-0">
    <div className="min-w-0 text-xs leading-5 text-foreground">
      <span className="text-muted-foreground">{props.item.verb}</span>{' '}
      <ActivitySubject {...props} />
      {detail && <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{detail}</p>}
      {props.item.management_change && <ManagementHistoryChanges entry={props.item.management_change} copy={props.copy.timeline} />}
    </div>
    <time className="pt-0.5 text-right text-[10px] tabular-nums text-muted-foreground">{format(parseISO(props.item.occurred_at), 'HH:mm')}</time>
  </div>
}

function GroupedActivity(props: Props) {
  const [open, setOpen] = useState(false)
  const activity = props.group.items[0]
  const count = String(props.group.items.length)
  const kind = props.copy.timeline[`activity_kind_${activity.kind}`]
  const summary = props.copy.timeline.activity_group_summary.replace('%{count}', count).replace('%{kind}', kind)
  const toggleLabel = (open ? props.copy.timeline.activity_group_collapse : props.copy.timeline.activity_group_expand).replace('%{count}', count)

  return <div data-testid="timeline-activity-group" className="min-w-0 pb-6">
    <div className="flex min-w-0 items-start gap-2">
      <ActorAvatar activity={activity} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <p className="min-w-0 text-sm leading-6 text-foreground"><span className="font-semibold">{activity.actor_name}</span>{' '}{summary}</p>
          <time className="ml-auto shrink-0 pt-1 text-[11px] tabular-nums text-muted-foreground">{format(parseISO(activity.occurred_at), 'HH:mm')}</time>
        </div>
        {!open && activity.subject && <p className="mt-0.5 truncate text-xs text-muted-foreground">{activity.subject}</p>}
        <button type="button" aria-expanded={open} onClick={() => setOpen((current) => !current)} className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground">
          {toggleLabel}
          <ChevronDown className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && <div className="mt-2 divide-y divide-border/70 border-l border-border pl-3">
          {props.group.items.map((item) => <ActivityDetail key={item.id} item={item} copy={props.copy} events={props.events} canManage={props.canManage} onEditEvent={props.onEditEvent} />)}
        </div>}
      </div>
    </div>
  </div>
}

export default function TimelineActivityEntry(props: Props) {
  if (props.group.items.length > 1) return <GroupedActivity {...props} />

  const item = props.group.items[0]
  const detail = detailText(item)

  return <div className="min-w-0 pb-7">
    <div className="flex min-w-0 items-start gap-2">
      <ActorAvatar activity={item} />
      <p className="min-w-0 text-sm leading-6 text-foreground">
        <span className="font-semibold">{item.actor_name}</span>{' '}{item.verb}{' '}
        <ActivitySubject item={item} copy={props.copy} events={props.events} canManage={props.canManage} onEditEvent={props.onEditEvent} />
      </p>
      <time className="ml-auto shrink-0 pt-1 text-[11px] tabular-nums text-muted-foreground">{format(parseISO(item.occurred_at), 'HH:mm')}</time>
    </div>
    {detail && <p className="ml-8 mt-0.5 truncate text-xs text-muted-foreground">{detail}</p>}
    {item.management_change && <div className="ml-8"><ManagementHistoryChanges entry={item.management_change} copy={props.copy.timeline} /></div>}
  </div>
}
